import { startOfToday, startOfTomorrow } from 'date-fns';
import { defineStore } from 'pinia';
import { Subject } from 'rxjs';

import { useOrderStore } from '@/features/orders/orderStore';
import { useProjectContributorStore } from '@/features/projectContributors/store/projectContributorStore';
import { useProjectStore } from '@/features/projects/projectStore';
import { useProjectTradeStore } from '@/features/projectTrades/projectTradeStore';

import { useScheduleState } from '../schedule/composables/useScheduleState';
import { useLocalOrderSchedulingEngineUtils } from '../schedule/engine/install';
import {
  AggregationResult,
  CapacitiesData,
  SummaryRowCellMouseEvent,
  WorkerThresholdExceededAlert,
} from './types';
import { useCapacitiesAccess } from './useCapacitiesAccess';
import {
  getAggregatedTradeSummariesForDate,
  getAggregatedTradeSummariesForTimespan,
  getCapacitiesData,
  getContributorGroupWorkerPeaks,
  getContributorGroupWorkerThresholdAlerts,
  getDailyPeaks,
  getProjectPeaks,
  getProjectWorkerThresholdAlerts,
  getVisibleOrdersMap,
} from './utils';

export const useCapacityStore = defineStore('capacity-store', () => {
  const { isStartDateDuringWorkingTime } = useLocalOrderSchedulingEngineUtils();
  const contributorStore = useProjectContributorStore();
  const orderStore = useOrderStore();
  const projectStore = useProjectStore();
  const projectTradeStore = useProjectTradeStore();
  const { scheduleIsLoaded } = useScheduleState();
  const { hasCapacitiesAccess } = useCapacitiesAccess();

  const summaryRowRendered$ = new Subject<string>();
  const summaryRowCellMouseEvents$ = new Subject<SummaryRowCellMouseEvent>();

  let dailyAggregationCache = new Map<
    string,
    {
      sumOfActivities: AggregationResult;
      sumOfWorkers: AggregationResult;
    }
  >();

  let timespanAggregationCache = new Map<
    string,
    {
      maxSumOfWorkers: AggregationResult;
      maxSumOfActivities: AggregationResult;
    }
  >();

  const clearCaches = () => {
    dailyAggregationCache = new Map();
    timespanAggregationCache = new Map();
  };

  const canShowCapacities = computed(() => hasCapacitiesAccess.value && scheduleIsLoaded.value);

  const capacitiesData = computed<CapacitiesData>(() => {
    if (!canShowCapacities.value) {
      return {
        dailyTradeSummaries: {},
        maxTradeAppearances: {},
        maxTradeWorkerCounts: {},
        maxContributorGroupWorkerCounts: {},
        timeRange: {
          start: startOfToday(),
          end: startOfTomorrow(),
        },
        tradesUsedForSummaries: new Map(),
      };
    }

    clearCaches();

    return getCapacitiesData(
      getVisibleOrdersMap(orderStore.orders),
      projectTradeStore.trades,
      contributorStore.contributorGroups,
      isStartDateDuringWorkingTime,
    );
  });

  const dailyTradeSummaries = computed(() => capacitiesData.value.dailyTradeSummaries);
  const maxTradeAppearances = computed(() => capacitiesData.value.maxTradeAppearances);
  const maxTradeWorkerCounts = computed(() => capacitiesData.value.maxTradeWorkerCounts);
  const summaryTimeRange = computed(() => capacitiesData.value.timeRange);
  const tradesUsedForSummaries = computed(() => capacitiesData.value.tradesUsedForSummaries);

  const aggregateTradeSummariesForDate = (date: Date) =>
    getAggregatedTradeSummariesForDate(dailyTradeSummaries.value, date, dailyAggregationCache);

  const aggregateTradeSummariesForTimespan = (startDate: Date, endDate: Date) =>
    getAggregatedTradeSummariesForTimespan(
      dailyTradeSummaries.value,
      startDate,
      endDate,
      timespanAggregationCache,
    );

  const dailyPeaks = computed(() => getDailyPeaks(dailyTradeSummaries.value));
  const projectPeaks = computed(() => getProjectPeaks(dailyPeaks.value));

  const contributorGroupWorkerPeaks = computed(() => {
    const allContributorGroups = [
      ...contributorStore.internalContributorGroups,
      ...contributorStore.externalContributorGroups,
    ];
    return getContributorGroupWorkerPeaks(
      capacitiesData.value.maxContributorGroupWorkerCounts,
      allContributorGroups.map((group) => group.id),
    );
  });

  const workerThresholdExceededAlertList = ref<WorkerThresholdExceededAlert[]>([]);

  watch(
    [
      () => projectStore.currentProject,
      () => capacitiesData.value.maxContributorGroupWorkerCounts,
      () => contributorStore.contributorGroups,
    ],
    async () => {
      if (!projectStore.currentProject || !contributorStore.contributorGroups.size) {
        workerThresholdExceededAlertList.value = [];
        return;
      }

      const contributorGroupAlerts = getContributorGroupWorkerThresholdAlerts(
        contributorStore.contributorGroups,
        capacitiesData.value.maxContributorGroupWorkerCounts,
      );

      const projectAlerts = getProjectWorkerThresholdAlerts(
        projectStore.currentProject,
        dailyPeaks.value,
      );

      workerThresholdExceededAlertList.value = [...contributorGroupAlerts, ...projectAlerts];
    },
    { immediate: true },
  );

  const workerThresholdExceededAlerts = computed<Map<string, WorkerThresholdExceededAlert>>(() => {
    return new Map(
      workerThresholdExceededAlertList.value.map((capacityCollision) => [
        capacityCollision.id,
        capacityCollision,
      ]),
    );
  });

  return {
    contributorGroupWorkerPeaks,
    dailyPeaks,
    dailyTradeSummaries,
    maxTradeAppearances,
    maxTradeWorkerCounts,
    projectPeaks,
    summaryRowCellMouseEvents$,
    summaryRowRendered$,
    summaryTimeRange,
    tradesUsedForSummaries,
    workerThresholdExceededAlertList,
    workerThresholdExceededAlerts,
    aggregateTradeSummariesForDate,
    aggregateTradeSummariesForTimespan,
  };
});

export type CapacityStore = ReturnType<typeof useCapacityStore>;
